import React from 'react';
import { ChevronRight, Home } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Standardized PageHeader component ensuring consistent title, actions,
 * breadcrumbs, and layout across all views in Stride.
 */
const PageHeader = ({
  title,
  subtitle,
  description, // alias for subtitle
  badge,
  breadcrumbs = [],
  actions,
  tabs,
  children,
  className = '',
}) => {
  const appliedSubtitle = subtitle || description;

  return (
    <header className={`w-full mb-6 ${className}`}>
      {/* Breadcrumbs Navigation */}
      {breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="mb-3 flex items-center gap-1.5 text-xs text-gray-500">
          <Link
            to="/dashboard"
            className="flex items-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Home size={13} className="mr-1" />
            Home
          </Link>

          {breadcrumbs.map((crumb, idx) => {
            const isLast = idx === breadcrumbs.length - 1;
            return (
              <React.Fragment key={crumb.label || idx}>
                <ChevronRight size={12} className="text-gray-300 shrink-0" />
                {isLast || !crumb.href ? (
                  <span className={`font-medium ${isLast ? 'text-gray-800' : 'text-gray-500'}`}>
                    {crumb.label}
                  </span>
                ) : (
                  <Link
                    to={crumb.href}
                    onClick={crumb.onClick}
                    className="text-gray-500 hover:text-gray-800 transition-colors"
                  >
                    {crumb.label}
                  </Link>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      )}

      {/* Main Title & Action Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-stride-heading">
              {title}
            </h1>
            {badge && <div className="shrink-0">{badge}</div>}
          </div>

          {appliedSubtitle && (
            <p className="mt-1 text-sm text-gray-500 font-normal leading-normal max-w-3xl">
              {appliedSubtitle}
            </p>
          )}
        </div>

        {/* Action Toolbar */}
        {actions && (
          <div className="flex items-center gap-2.5 flex-wrap shrink-0">
            {actions}
          </div>
        )}
      </div>

      {/* Tabs or Custom Bottom Slot */}
      {tabs && <div className="mt-4 border-b border-gray-200">{tabs}</div>}
      {children && <div className="mt-4">{children}</div>}
    </header>
  );
};

export default PageHeader;

